import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban, ShieldCheck, Inbox, PackageCheck, Bell, AlertTriangle,
  ArrowUpRight, Activity, Bot, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Stat, Card, CardHeader, PriorityBadge, Label, TimeAgo, EmptyState } from "@/components/common";

export default function Dashboard() {
  const { companyParam, companyId, setDirectiveOpen } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", companyId],
    queryFn: async () => (await api.get("/dashboard", { params: companyParam })).data,
    refetchInterval: 20000,
  });

  if (isLoading || !data) return <div className="text-sm text-ink-muted">Loading control tower…</div>;
  const { stats, attention, breakdown, recent_activity, recent_runs } = data;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <Label>Control Tower</Label>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">
            Good day, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-ink-muted mt-1">Here's everything that needs your attention across the operation.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat testId="stat-active-jobs" label="Active Jobs" value={stats.active_jobs} icon={FolderKanban} onClick={() => navigate("/jobs")} />
        <Stat testId="stat-approvals" label="Pending Approvals" value={stats.pending_approvals} icon={ShieldCheck} accent={stats.pending_approvals > 0 ? "text-st-warning" : "text-ink"} onClick={() => navigate("/approvals")} />
        <Stat testId="stat-directives" label="Open Directives" value={stats.open_directives} icon={Inbox} onClick={() => navigate("/directives")} />
        <Stat testId="stat-delivered" label="Delivered" value={stats.delivered} icon={PackageCheck} accent="text-st-success" />
        <Stat testId="stat-notifications" label="Unread Alerts" value={stats.unread_notifications} icon={Bell} accent={stats.unread_notifications > 0 ? "text-brand" : "text-ink"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attention */}
        <div className="lg:col-span-2 space-y-4">
          <Card testId="attention-panel">
            <CardHeader title="Needs Your Attention" icon={AlertTriangle} action={<span className="text-[11px] font-mono text-ink-faint">{attention.length} items</span>} />
            {attention.length === 0 ? (
              <div className="p-4"><EmptyState icon={ShieldCheck} title="Nothing waiting on you" hint="All directives are processed and no approvals are pending." /></div>
            ) : (
              <div className="divide-y divide-border">
                {attention.map((a) => (
                  <button
                    key={a.job_id}
                    onClick={() => navigate(a.type === "approval" ? `/jobs/${a.job_id}` : `/jobs/${a.job_id}`)}
                    data-testid={`attention-item-${a.job_id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted transition-colors group"
                  >
                    <PriorityBadge value={a.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{a.name}</div>
                      <div className="text-xs text-ink-muted">{a.client} · {a.reason}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-ink-faint group-hover:text-brand group-hover:translate-x-0.5 transition-all" />
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Recent activity */}
          <Card testId="activity-panel">
            <CardHeader title="Live Activity" icon={Activity} action={<span className="w-2 h-2 bg-st-success live-dot" />} />
            <div className="divide-y divide-border max-h-80 overflow-y-auto">
              {recent_activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 px-4 py-2.5">
                  <div className="w-1.5 h-1.5 bg-brand mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink">{a.summary}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] font-semibold text-ink-muted">{a.actor}</span>
                      <TimeAgo iso={a.created_at} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <Card testId="breakdown-panel">
            <CardHeader title="By Company" icon={FolderKanban} />
            <div className="divide-y divide-border">
              {breakdown.map((b) => (
                <div key={b.company_id} className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5" style={{ backgroundColor: b.color }} />
                    <span className="text-sm font-semibold text-ink flex-1">{b.name}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[["Active", b.active], ["Approvals", b.approvals], ["Clients", b.clients]].map(([l, v]) => (
                      <div key={l} className="bg-surface-muted py-2">
                        <div className="font-heading text-xl font-bold text-ink">{v}</div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-faint">{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card testId="runs-panel">
            <CardHeader title="Recent Agent Runs" icon={Bot} />
            <div className="divide-y divide-border max-h-72 overflow-y-auto">
              {recent_runs.map((r) => (
                <div key={r.id} className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Bot className="w-3.5 h-3.5 text-brand shrink-0" />
                    <span className="text-xs font-semibold text-ink truncate flex-1">{r.agent_name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-st-success font-semibold">{r.status}</span>
                  </div>
                  <div className="text-xs text-ink-muted mt-0.5 pl-5">{r.summary}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
